/** Hosted sites (/sites): upload, browse (list or grid), filter, preview, share, edit description / AI summary, versions and rollback */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ExternalLink, Globe, Search, Upload } from 'lucide-react'
import { useDeleteSite, useHostedSites, useRegenerateSiteSummary, useUpdateSite, type HostedSite } from '../hooks/use-hosted-sites'
import { useT } from '../i18n'
import { cn } from '../lib/utils'
import { Modal } from '../components/ui/modal'
import { buttonDangerClass, buttonPrimaryClass, buttonSecondaryClass, errorTextClass, inputClass } from '../components/ui/styles'
import { FilterBar, NO_SITE_FILTERS } from '../components/hosted-sites/filter-bar'
import { HostedSiteCard } from '../components/hosted-sites/hosted-site-card'
import { HostedSiteRow, type HostedSiteRowProps } from '../components/hosted-sites/hosted-site-row'
import { UploadPanel } from '../components/hosted-sites/upload-panel'
import { ViewToggle, type SitesView } from '../components/hosted-sites/view-toggle'
import { applySiteFilters, createdAfterFor, parseSiteFilters, sitePublicUrl, type SiteUrlFilters } from '../components/hosted-sites/utils'

const VIEW_STORAGE_KEY = 'hosted-sites-view'
/** A regenerated summary arrives through background refetches; stop showing the spinner after this long */
const SUMMARY_WAIT_MS = 30_000

function readStoredView(): SitesView {
  try {
    return localStorage.getItem(VIEW_STORAGE_KEY) === 'card' ? 'card' : 'list'
  } catch {
    return 'list'
  }
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

export function HostedSitesPage() {
  const t = useT()
  const [searchParams, setSearchParams] = useSearchParams()
  const urlFilters = parseSiteFilters(searchParams)
  const { status, siteType, dateRange } = urlFilters

  const [view, setView] = useState<SitesView>(readStoredView)
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, view)
    } catch {
      // Storage can be unavailable (private mode); the choice then lasts for this visit only
    }
  }, [view])

  const [search, setSearch] = useState('')
  const [showUpload, setShowUpload] = useState(false)
  const [previewSite, setPreviewSite] = useState<HostedSite | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<HostedSite | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [pendingSummary, setPendingSummary] = useState<{ id: string; baseline: string | null } | null>(null)
  const [summaryError, setSummaryError] = useState<{ id: string; message: string } | null>(null)

  // Memoized so the query key stays stable between renders (Date.now() would otherwise refetch forever)
  const filters = useMemo(
    () => ({
      search: search.trim() || undefined,
      status: status || undefined,
      siteType: siteType || undefined,
      createdAfter: createdAfterFor(dateRange, Date.now()),
    }),
    [search, status, siteType, dateRange],
  )

  const { data, isLoading, isError, refetch } = useHostedSites(filters)
  const updateSite = useUpdateSite(filters)
  const deleteSite = useDeleteSite()
  const regenerateSummary = useRegenerateSiteSummary()

  const sites = useMemo(() => data?.sites ?? [], [data])
  const total = data?.total ?? 0
  const hasActiveFilters = status !== '' || siteType !== '' || dateRange !== 'all'

  const applyFilters = (next: SiteUrlFilters) => setSearchParams((prev) => applySiteFilters(prev, next))

  // Clear the summary spinner once a refetch shows a new summary, or after the cap
  useEffect(() => {
    if (!pendingSummary) return
    const current = sites.find((s) => s.id === pendingSummary.id)
    if (current?.summary && current.summary !== pendingSummary.baseline) {
      setPendingSummary(null)
      return
    }
    const cap = setTimeout(() => setPendingSummary(null), SUMMARY_WAIT_MS)
    return () => clearTimeout(cap)
  }, [pendingSummary, sites])

  const handleRegenerate = useCallback(
    async (id: string) => {
      setSummaryError(null)
      setPendingSummary({ id, baseline: sites.find((s) => s.id === id)?.summary ?? null })
      try {
        await regenerateSummary.mutateAsync(id)
      } catch (err) {
        setSummaryError({ id, message: t('sites.summary.failed', { message: errorMessage(err) }) })
        setPendingSummary(null)
      }
    },
    [regenerateSummary, sites, t],
  )

  const handleSaveDescription = useCallback(
    async (site: HostedSite, description: string | null) => {
      try {
        await updateSite.mutateAsync({ id: site.id, description })
        toast.success(t('sites.description.saved'))
      } catch (err) {
        toast.error(t('sites.description.saveFailed', { message: errorMessage(err) }))
        throw err
      }
    },
    [updateSite, t],
  )

  const handleCopyLink = useCallback(
    async (site: HostedSite) => {
      try {
        await navigator.clipboard.writeText(sitePublicUrl(site))
        setCopiedId(site.id)
        toast.success(t('sites.copied'))
        setTimeout(() => setCopiedId((current) => (current === site.id ? null : current)), 2000)
      } catch {
        toast.error(t('sites.copyFailed'))
      }
    },
    [t],
  )

  const confirmDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteSite.mutateAsync(deleteTarget.id)
      toast.success(t('sites.delete.done', { name: deleteTarget.filename }))
      if (previewSite?.id === deleteTarget.id) setPreviewSite(null)
      setDeleteTarget(null)
    } catch (err) {
      toast.error(t('sites.delete.failed', { message: errorMessage(err) }))
    }
  }

  const itemProps = (site: HostedSite): HostedSiteRowProps => ({
    site,
    expanded: expandedId === site.id,
    onToggleExpanded: (id) => setExpandedId((prev) => (prev === id ? null : id)),
    copied: copiedId === site.id,
    onCopyLink: (s) => void handleCopyLink(s),
    onPreview: setPreviewSite,
    onDelete: setDeleteTarget,
    onSaveDescription: handleSaveDescription,
    summaryPending: pendingSummary?.id === site.id,
    summaryError: summaryError?.id === site.id ? summaryError.message : null,
    onRegenerate: (id) => void handleRegenerate(id),
  })

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-4 py-6 md:px-8 md:py-8">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-fg">{t('sites.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('sites.subtitle')}</p>
        </div>
        <button type="button" onClick={() => setShowUpload((v) => !v)} aria-expanded={showUpload} className={buttonPrimaryClass}>
          <Upload className="h-4 w-4" aria-hidden="true" />
          {t('sites.upload')}
        </button>
      </header>

      {showUpload && <UploadPanel onClose={() => setShowUpload(false)} />}

      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative min-w-0 lg:w-64">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-subtle" aria-hidden="true" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label={t('sites.search')}
            placeholder={t('sites.search')}
            className={cn(inputClass, 'pl-9')}
          />
        </div>
        <FilterBar status={status} siteType={siteType} dateRange={dateRange} onChange={applyFilters} />
        <div className="flex items-center justify-between gap-3 lg:ml-auto">
          <span className="text-sm text-fg-subtle" aria-live="polite">
            {total === 1 ? t('sites.countOne') : t('sites.count', { count: total })}
          </span>
          <ViewToggle view={view} onChange={setView} />
        </div>
      </div>

      {isLoading ? (
        <p className="py-10 text-center text-sm text-fg-subtle">{t('common.loading')}</p>
      ) : isError ? (
        <div className={errorTextClass} role="alert">
          {t('sites.loadError')}{' '}
          <button type="button" onClick={() => void refetch()} className="cursor-pointer font-medium underline underline-offset-2">
            {t('common.retry')}
          </button>
        </div>
      ) : sites.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border-strong px-4 py-12 text-center">
          <Globe className="h-8 w-8 text-fg-subtle" aria-hidden="true" />
          <p className="text-sm text-fg-muted">{hasActiveFilters || search ? t('sites.noMatch') : t('sites.empty')}</p>
          {hasActiveFilters ? (
            <button type="button" onClick={() => applyFilters(NO_SITE_FILTERS)} className="cursor-pointer text-sm text-accent-text underline-offset-2 hover:underline">
              {t('sites.filter.reset')}
            </button>
          ) : (
            !search && <p className="text-xs text-fg-subtle">{t('sites.emptyHint')}</p>
          )}
        </div>
      ) : view === 'list' ? (
        <ul aria-label={t('sites.title')} className="space-y-2">
          {sites.map((site) => (
            <HostedSiteRow key={site.id} {...itemProps(site)} />
          ))}
        </ul>
      ) : (
        <ul aria-label={t('sites.title')} className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {sites.map((site) => (
            <HostedSiteCard key={site.id} {...itemProps(site)} />
          ))}
        </ul>
      )}

      {previewSite && (
        <Modal title={t('sites.preview.title', { name: previewSite.filename })} onClose={() => setPreviewSite(null)} widthClass="max-w-5xl">
          <div className="space-y-3">
            <a
              href={sitePublicUrl(previewSite)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-accent-text hover:underline"
            >
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
              {t('sites.preview.open')}
            </a>
            <iframe
              src={sitePublicUrl(previewSite)}
              title={previewSite.filename}
              className="h-[65vh] w-full rounded-md border border-border bg-surface"
              sandbox="allow-scripts"
            />
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <Modal title={t('sites.delete.title')} onClose={() => setDeleteTarget(null)}>
          <div className="space-y-4">
            <p className="text-sm text-fg-muted">{t('sites.delete.body', { name: deleteTarget.filename })}</p>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setDeleteTarget(null)} className={buttonSecondaryClass}>
                {t('common.cancel')}
              </button>
              <button type="button" data-autofocus onClick={() => void confirmDelete()} disabled={deleteSite.isPending} className={buttonDangerClass}>
                {deleteSite.isPending ? t('sites.delete.deleting') : t('sites.delete.confirm')}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
